<script>
import axios from '@/utils/axios';
import { Offcanvas } from 'bootstrap';

const MONTHS = [
  { v: 1, l: 'January' }, { v: 2, l: 'February' }, { v: 3, l: 'March' },
  { v: 4, l: 'April' }, { v: 5, l: 'May' }, { v: 6, l: 'June' },
  { v: 7, l: 'July' }, { v: 8, l: 'August' }, { v: 9, l: 'September' },
  { v: 10, l: 'October' }, { v: 11, l: 'November' }, { v: 12, l: 'December' },
];

export default {
  emits: ['created'],
  data() {
    const now = new Date();
    return {
      saving: false,
      months: MONTHS,
      years: [2024, 2025, 2026, 2027],
      form: {
        month: now.getMonth() + 1,
        year: now.getFullYear(),
        working_days: '',
        remarks: '',
      },
      errors: {},
      oc: null,
    };
  },
  methods: {
    open() {
      this.reset();
      this.suggestWorkingDays();
      this.oc = Offcanvas.getOrCreateInstance(this.$refs.oc);
      this.oc.show();
    },
    close() { this.oc?.hide(); },
    reset() {
      const now = new Date();
      this.form = { month: now.getMonth() + 1, year: now.getFullYear(), working_days: '', remarks: '' };
      this.errors = {};
    },
    // Total days in month minus Sundays (most common default)
    suggestWorkingDays() {
      const { month, year } = this.form;
      if (!month || !year) return;
      const totalDays = new Date(year, month, 0).getDate();
      let sundays = 0;
      for (let d = 1; d <= totalDays; d++) {
        if (new Date(year, month - 1, d).getDay() === 0) sundays++;
      }
      this.form.working_days = totalDays - sundays;
    },
    errText(f) { const v = this.errors[f]; if (!v) return ''; return Array.isArray(v) ? v[0] : v; },
    validate() {
      const e = {};
      if (!this.form.month) e.month = 'Month is required';
      if (!this.form.year) e.year = 'Year is required';
      if (!this.form.working_days) e.working_days = 'Working days is required';
      else if (this.form.working_days < 1 || this.form.working_days > 31) e.working_days = 'Must be between 1 and 31';
      this.errors = e;
      return !Object.keys(e).length;
    },
    async submit() {
      if (!this.validate()) return;
      this.saving = true;
      try {
        const res = await axios.post('/hrms/payroll', {
          month: this.form.month,
          year: this.form.year,
          working_days: this.form.working_days,
          remarks: this.form.remarks || null,
        });
        this.$swalToast(res.data.message || 'Payroll run created.');
        this.$emit('created', res.data.data);
        this.close();
      } catch (error) {
        if (error.type === 'validation') this.errors = error.errors;
        else this.$swalToast(error.message, 'error');
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<template>
  <div ref="oc" class="offcanvas offcanvas-end pr-oc" tabindex="-1" id="hrm_payroll_create_modal">
    <div class="offcanvas-header">
      <div class="d-flex align-items-center gap-2">
        <span class="pr-oc-ico"><i class="ti ti-calculator"></i></span>
        <h5 class="offcanvas-title mb-0">New Payroll Run</h5>
      </div>
      <button type="button" class="btn-close" data-bs-dismiss="offcanvas"></button>
    </div>

    <div class="offcanvas-body">
      <div class="row g-2 mb-3">
        <div class="col-6">
          <label class="pr-lbl">Month <span class="text-danger">*</span></label>
          <select class="form-select" v-model="form.month" :class="{ 'is-invalid': errText('month') }" @change="suggestWorkingDays">
            <option v-for="m in months" :key="m.v" :value="m.v">{{ m.l }}</option>
          </select>
          <div v-if="errText('month')" class="pr-err">{{ errText('month') }}</div>
        </div>
        <div class="col-6">
          <label class="pr-lbl">Year <span class="text-danger">*</span></label>
          <select class="form-select" v-model="form.year" :class="{ 'is-invalid': errText('year') }" @change="suggestWorkingDays">
            <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
          </select>
          <div v-if="errText('year')" class="pr-err">{{ errText('year') }}</div>
        </div>
      </div>

      <div class="mb-3">
        <label class="pr-lbl">Working Days <span class="text-danger">*</span></label>
        <input type="number" min="1" max="31" class="form-control" v-model.number="form.working_days" :class="{ 'is-invalid': errText('working_days') }" />
        <div v-if="errText('working_days')" class="pr-err">{{ errText('working_days') }}</div>
        <small class="pr-hint">Auto-suggested (total days minus Sundays). Adjust if needed.</small>
      </div>

      <div class="mb-3">
        <label class="pr-lbl">Remarks</label>
        <textarea class="form-control" rows="2" maxlength="500" v-model="form.remarks" placeholder="Optional note"></textarea>
      </div>

      <div class="pr-info">
        <i class="ti ti-info-circle me-1"></i>
        This creates a draft run. You can process it after creation to calculate salaries.
      </div>
    </div>

    <div class="pr-oc-footer">
      <button type="button" class="btn btn-light" data-bs-dismiss="offcanvas">Cancel</button>
      <button type="button" class="btn btn-primary" :disabled="saving" @click="submit">
        <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
        {{ saving ? 'Creating…' : 'Create Run' }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.pr-oc { width: 480px; max-width: 92vw; }
.offcanvas-header { border-bottom: 1px solid #f1f5f9; padding: 18px 22px; }
.offcanvas-title { font-size: 16px; font-weight: 700; color: #0f172a; }
.pr-oc-ico { width: 36px; height: 36px; border-radius: 10px; flex-shrink: 0;
  background: linear-gradient(135deg, #4f46e5, #7c3aed); color: #fff;
  display: inline-flex; align-items: center; justify-content: center; font-size: 18px; }
.offcanvas-body { padding: 22px; }
.pr-lbl { font-size: 12px; font-weight: 600; color: #475569; margin-bottom: 5px; display: block; }
.form-control, .form-select { border-radius: 10px; border-color: #e2e8f0; font-size: 13.5px; padding: 9px 12px; }
.form-control:focus, .form-select:focus { border-color: #a5b4fc; box-shadow: 0 0 0 3px rgba(79,70,229,.1); }
.pr-err { font-size: 11px; color: #dc2626; margin-top: 4px; }
.pr-hint { font-size: 11px; color: #94a3b8; margin-top: 4px; display: block; }
.pr-info { background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 10px; padding: 12px; font-size: 12px; color: #1e40af; }
.pr-oc-footer { display: flex; justify-content: flex-end; gap: 10px; padding: 16px 22px; border-top: 1px solid #f1f5f9; margin-top: auto; }
.pr-oc-footer .btn { border-radius: 10px; font-size: 13px; font-weight: 600; padding: 9px 18px; }
.btn-primary { background: linear-gradient(135deg, #4f46e5, #7c3aed); border: none; }
.btn-light { background: #f8fafc; border: 1px solid #e2e8f0; color: #475569; }
</style>